import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  ViewChild,
  ViewContainerRef,
  Type,
  ComponentRef,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import {
  HeaderRenderer,
  HeaderRendererParams,
  BuiltInHeaderRenderer,
} from '../../core/interfaces/header-renderer.interface';

/**
 * Header Renderer Loader Component
 *
 * Dynamically loads and renders custom header renderer components.
 * Supports both Angular component renderers and built-in header styles.
 *
 * @example
 * ```html
 * <app-header-renderer-loader
 *   [renderer]="'text-icon'"
 *   [params]="headerParams">
 * </app-header-renderer-loader>
 * ```
 */
@Component({
  selector: 'app-header-renderer-loader',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, MatBadgeModule],
  template: `
    <ng-container #rendererContainer></ng-container>

    <!-- Built-in: Text Only -->
    <ng-container *ngIf="builtInRenderer === 'text'">
      <span class="header-text">{{ displayName }}</span>
    </ng-container>

    <!-- Built-in: Text with Icon -->
    <ng-container *ngIf="builtInRenderer === 'text-icon'">
      <div class="flex items-center gap-2">
        <mat-icon *ngIf="icon" [class]="iconClass">{{ icon }}</mat-icon>
        <span class="header-text">{{ displayName }}</span>
      </div>
    </ng-container>

    <!-- Built-in: Icon Only -->
    <ng-container *ngIf="builtInRenderer === 'icon-only'">
      <mat-icon
        *ngIf="icon"
        [class]="iconClass"
        [matTooltip]="displayName">
        {{ icon }}
      </mat-icon>
    </ng-container>

    <!-- Built-in: Badge -->
    <ng-container *ngIf="builtInRenderer === 'badge'">
      <div class="flex items-center gap-2">
        <mat-icon *ngIf="icon" [class]="iconClass">{{ icon }}</mat-icon>
        <span
          class="header-text"
          [matBadge]="badgeValue"
          [matBadgeColor]="badgeColor">
          {{ displayName }}
        </span>
      </div>
    </ng-container>

    <!-- Built-in: Tooltip -->
    <ng-container *ngIf="builtInRenderer === 'tooltip'">
      <div class="flex items-center gap-2" [matTooltip]="tooltipText || ''">
        <mat-icon *ngIf="icon" [class]="iconClass">{{ icon }}</mat-icon>
        <span class="header-text">{{ displayName }}</span>
        <mat-icon class="text-gray-400" style="font-size: 16px; width: 16px; height: 16px;">
          info
        </mat-icon>
      </div>
    </ng-container>
  `,
  styles: [`
    :host {
      display: flex;
      align-items: center;
      width: 100%;
    }

    .header-text {
      font-weight: 600;
      font-size: inherit;
    }
  `],
})
export class HeaderRendererLoaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() renderer?: Type<HeaderRenderer<any>> | BuiltInHeaderRenderer;
  @Input() params!: HeaderRendererParams<any>;

  @ViewChild('rendererContainer', { read: ViewContainerRef })
  container!: ViewContainerRef;

  customRenderer: ComponentRef<HeaderRenderer<any>> | null = null;
  builtInRenderer: BuiltInHeaderRenderer | null = null;

  // Header display data
  displayName: string = '';
  icon?: string;
  iconClass?: string;
  badgeValue?: string | number;
  badgeColor?: 'primary' | 'accent' | 'warn';
  tooltipText?: string;

  ngOnInit(): void {
    // Extract display name
    this.displayName = this.params.displayName || this.params.colDef.headerName || '';

    // Extract parameters
    const rendererParams = this.params.colDef.headerRendererParams || {};
    this.icon = rendererParams['icon'];
    this.iconClass = rendererParams['iconClass'] || '';
    this.badgeValue = rendererParams['badgeValue'];
    this.badgeColor = rendererParams['badgeColor'] || 'primary';
    this.tooltipText = rendererParams['tooltipText'] || this.params.colDef.headerTooltip;

    if (!this.renderer) {
      // No renderer - use default text
      this.builtInRenderer = 'text';
      return;
    }

    if (typeof this.renderer === 'string') {
      // Built-in renderer
      this.builtInRenderer = this.renderer;
    }
    // Component renderer will be loaded in ngAfterViewInit via container
  }

  ngAfterViewInit(): void {
    if (this.renderer && typeof this.renderer !== 'string') {
      // Load custom component renderer
      this.loadCustomRenderer(this.renderer);
    }
  }

  ngOnDestroy(): void {
    if (this.customRenderer) {
      this.customRenderer.destroy();
    }
  }

  /**
   * Dynamically loads a custom header renderer component
   */
  private loadCustomRenderer(
    rendererType: Type<HeaderRenderer<any>>
  ): void {
    if (!this.container) {
      console.error('Container not available for custom header renderer');
      return;
    }

    this.container.clear();
    this.customRenderer = this.container.createComponent(rendererType);

    // Pass params to the custom renderer
    this.customRenderer.instance.params = this.params;

    // Trigger change detection
    this.customRenderer.changeDetectorRef.detectChanges();
  }
}
